import { Row, Col, Dropdown } from "react-bootstrap";
import { Toast, ToastContainer } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { ReactComponent as DownArrowIc } from "../../assets/svg/down_arrow_ics.svg";
import { ReactComponent as RarIc } from "../../assets/svg/rar_ic.svg";
import { ReactComponent as LarIc } from "../../assets/svg/lar_ic.svg";

/**
 * Required field marker component
 */
export const RequiredMark = () => <span style={{ color: "#FF4444" }}>*</span>;

/**
 * Reusable form field component
 * @param {Object} props
 * @param {string} props.label - Field label text
 * @param {boolean} props.required - Show required marker (default: false)
 * @param {string} props.type - Input type (default: "text")
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {string} props.error - Error message
 * @param {string} props.placeholder - Placeholder text (default: "Enter here")
 * @param {Object|null} props.colSize - Bootstrap Col sizes, pass null for plain div wrapper (default: { xs: 12, sm: 12 })
 * @param {string} props.inputClassName - Custom input CSS class (default: "input-field-new mt-1 w-100")
 * @param {string} props.wrapperClassName - Custom wrapper CSS class (default: "mt-3")
 * @param {Object} props.wrapperStyle - Inline styles for the wrapper element
 */
export const FormField = ({
    label,
    required = false,
    type = "text",
    value,
    onChange,
    error,
    placeholder = "Enter here",
    colSize = { xs: 12, sm: 12 },
    inputClassName = "input-field-new mt-1 w-100",
    wrapperClassName = "mt-3",
    wrapperStyle,
    ...rest
}) => {
    const content = (
        <>
            <div className="field-title">
                {label}
                {required && <RequiredMark />}
            </div>
            <input
                type={type}
                placeholder={placeholder}
                className={inputClassName}
                value={value}
                onChange={onChange}
                {...rest}
            />
            <span className="field-error">{error}</span>
        </>
    );

    if (colSize === null) {
        return <div className={wrapperClassName} style={wrapperStyle}>{content}</div>;
    }

    return <Col {...colSize} className={wrapperClassName} style={wrapperStyle}>{content}</Col>;
};

/**
 * Circular Loader component for loading states
 * @param {Object} props - Component props
 * @param {number} props.size - Loader size in pixels (default: 20)
 * @param {string} props.color - Loader color (default: #ffffff)
 */
export const CircularLoader = ({ size = 20, color = "#ffffff" }) => (
    <>
        <style>
            {`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}
        </style>
        <div
            style={{
                border: `3px solid rgba(255, 255, 255, 0.3)`,
                borderTop: `3px solid ${color}`,
                borderRadius: "50%",
                width: `${size}px`,
                height: `${size}px`,
                animation: "spin 1s linear infinite",
            }}
        />
    </>
);

/**
 * Reusable Pagination component
 */
export const CommonPagination = ({
    itemsPerPage,
    setItemsPerPage,
    pageCount,
    onPageChange,
    forcePage,
    rowsPerPageOptions = [10, 25, 50, 75, 100]
}) => {
    return (
        <div className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
            <Dropdown>
                <span className="pagination-color">Records per page: &nbsp;</span>
                <Dropdown.Toggle id="dropdown-basic" className="bg-transparent pagination-color border-0 custom-dropdown-toggle p-0">
                    <span className="bg-white textcolor no-of-rows rounded-3 px-2 py-2">
                        {itemsPerPage} <DownArrowIc />
                    </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="my-2" style={{ minWidth: "unset", width: "30%" }}>
                    {rowsPerPageOptions.map((size) => (
                        <Dropdown.Item
                            key={size}
                            onClick={() => setItemsPerPage(size)}
                            active={itemsPerPage === size}
                            className="text-center"
                        >
                            {size}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>

            <div className="d-flex align-items-center gap-2">
                <ReactPaginate
                    previousLabel={
                        <div className="rounded-5 bg-white d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px" }}>
                            <LarIc />
                        </div>
                    }
                    nextLabel={
                        <div className="rounded-5 bg-white d-flex align-items-center justify-content-center" style={{ width: "32px", height: "32px" }}>
                            <RarIc />
                        </div>
                    }
                    breakLabel={"..."}
                    pageCount={pageCount}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={3}
                    onPageChange={onPageChange}
                    forcePage={forcePage}
                    containerClassName={"pagination"}
                    activeClassName={"active"}
                />
            </div>
        </div>
    );
};

const CommonSnackbar = ({
    show,
    onClose,
    message,
    isSuccess = true,
    position = "top-end",
    delay = 3000,
}) => {
    return (
        <ToastContainer position={position}>
            <Toast
                className="d-inline-block m-1"
                bg={isSuccess ? "success" : "danger"}
                show={show}
                onClose={onClose}
                delay={delay}
                autohide
            >
                <Toast.Body style={{ color: "#ffffff" }}>
                    {message}
                </Toast.Body>
            </Toast>
        </ToastContainer>
    );
};

export default CommonSnackbar;
